/**
 * InjuryHealingDialog
 * --------------------
 * Affiché automatiquement quand l'utilisateur clique sur "Marquer guérie".
 * Récupère la réponse Coach YUMIX (intro + 2 actions) et applique l'action choisie.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, PartyPopper } from 'lucide-react';
import { api } from '../context/AppContext';
import { toast } from 'sonner';

const InjuryHealingDialog = ({ open, onClose, payload, onResolved }) => {
  const [submittingAction, setSubmittingAction] = useState(null);

  if (!payload) return null;
  const { dialog, zone, zone_label } = payload;

  const handleAction = async (actionId) => {
    setSubmittingAction(actionId);
    try {
      const res = await api.post('/injuries/heal-action', { zone, action: actionId });
      const data = res.data || {};
      toast.success(data.message || 'Action appliquée');
      onResolved?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'application de l\'action');
    } finally {
      setSubmittingAction(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-emerald-500/40 max-w-md" data-testid="healing-dialog">
        <DialogHeader>
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 rounded-full bg-emerald-500/20 border-2 border-emerald-500/40">
            <PartyPopper className="w-8 h-8 text-emerald-400" />
          </div>
          <DialogTitle className="text-center font-heading text-2xl uppercase tracking-tight" data-testid="healing-title">
            {dialog.title}
          </DialogTitle>
          <DialogDescription className="text-center text-sm leading-relaxed pt-2 space-y-3">
            <span className="block">{dialog.intro}</span>
            <span className="block font-medium" dangerouslySetInnerHTML={{ __html: dialog.question.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex flex-col gap-2">
          {dialog.actions.map((action) => (
            <Button
              key={action.id}
              onClick={() => handleAction(action.id)}
              disabled={!!submittingAction}
              variant={action.primary ? 'default' : 'outline'}
              className={action.primary ? 'bg-emerald-500 hover:bg-emerald-600 text-white font-bold' : ''}
              data-testid={`healing-action-${action.id}`}
            >
              {submittingAction === action.id ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              <div className="flex flex-col items-start text-left flex-1">
                <span className="font-bold">{action.label}</span>
                <span className="text-[11px] opacity-80 font-normal">{action.description}</span>
              </div>
            </Button>
          ))}
        </div>

        <p className="text-center text-[10px] text-muted-foreground mt-3">
          Zone concernée : <strong>{zone_label}</strong>
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default InjuryHealingDialog;
